import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, User, Phone, Mail, MapPin, Calendar, DollarSign,
  FileText, MessageSquare, Star, Clock, ChevronRight,
  X, Plus, ArrowLeft, CreditCard, History, Users,
  ClipboardList, StickyNote, Download, Filter, RefreshCw,
  Edit, Trash2, Archive, Save, Truck, Package, Car, Shirt, Home
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../services/api';

// Service type badges for customer profiles
const customerServiceBadges = {
  'rides': { color: 'bg-purple-100 text-purple-700 border-purple-300', icon: Car, label: 'Rides' },
  'laundry': { color: 'bg-[#F4C2C2]/15 text-pink-700 border-[#F4C2C2]/30', icon: Shirt, label: 'Laundry' },
  'errands': { color: 'bg-amber-100 text-amber-700 border-[var(--warning)]/30', icon: Package, label: 'Errands' },
  'courier': { color: 'bg-[var(--success)]/15 text-green-700 border-[var(--success)]/30', icon: Truck, label: 'Courier' },
};

// Determine service types from booking history
const getCustomerServiceTypes = (customer) => {
  // Check if customer has services_used array or derive from booking history
  if (customer.services_used && customer.services_used.length > 0) {
    return customer.services_used;
  }
  // Default: return based on last service if available, or empty
  if (customer.last_service_type) {
    return [customer.last_service_type];
  }
  return [];
};

const CustomerFilesTab = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerFile, setCustomerFile] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  
  // New filter states
  const [filterServiceType, setFilterServiceType] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active'); // active, archived, all
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchCustomers();
    }, 300);
    return () => clearTimeout(delaySearch);
     
  }, [searchQuery]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const response = await api.get(`/customer/files${params}`);
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCustomer = () => {
    if (customerFile) {
      setEditData({
        full_name: customerFile.customer?.full_name || '',
        email: customerFile.customer?.email || '',
        phone: customerFile.customer?.phone || '',
        address: customerFile.customer?.address || '',
        city: customerFile.customer?.city || '',
        state: customerFile.customer?.state || '',
        zip_code: customerFile.customer?.zip_code || ''
      });
      setEditMode(true);
    }
  };

  const handleSaveCustomer = async () => {
    try {
      await api.put(`/customer/files/${selectedCustomer.id}`, editData);
      toast.success('Customer updated');
      setEditMode(false);
      // Refresh the file
      const response = await api.get(`/customer/files/${selectedCustomer.id}`);
      setCustomerFile(response.data);
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to update customer');
    }
  };

  const handleArchiveCustomer = async (customerId) => {
    if (!window.confirm('Archive this customer? They will be moved to the archive and can be restored later.')) return;
    try {
      await api.post(`/customer/files/${customerId}/archive`);
      toast.success('Customer archived');
      setSelectedCustomer(null);
      setCustomerFile(null);
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to archive customer');
    }
  };

  const handleRestoreCustomer = async (customerId) => {
    try {
      await api.post(`/customer/files/${customerId}/restore`);
      toast.success('Customer restored');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to restore customer');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Permanently delete this customer file? This action cannot be undone.')) return;
    try {
      await api.delete(`/customer/files/${customerId}`);
      toast.success('Customer deleted');
      setSelectedCustomer(null);
      setCustomerFile(null);
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const handleBulkArchive = async () => {
    if (selectedCustomers.length === 0) return;
    if (!window.confirm(`Archive ${selectedCustomers.length} customers?`)) return;
    try {
      await Promise.all(selectedCustomers.map(id => api.post(`/customer/files/${id}/archive`)));
      toast.success(`${selectedCustomers.length} customers archived`);
      setSelectedCustomers([]);
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to archive some customers');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCustomers.length === 0) return;
    if (!window.confirm(`Permanently delete ${selectedCustomers.length} customers? This cannot be undone.`)) return;
    try {
      await Promise.all(selectedCustomers.map(id => api.delete(`/customer/files/${id}`)));
      toast.success(`${selectedCustomers.length} customers deleted`);
      setSelectedCustomers([]);
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete some customers');
    }
  };

  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const selectAllCustomers = () => {
    if (selectedCustomers.length === filteredCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(filteredCustomers.map(c => c.id));
    }
  };

  // Filter customers based on selected filters
  const filteredCustomers = customers.filter(customer => {
    // Status filter
    if (filterStatus === 'active' && customer.is_archived) return false;
    if (filterStatus === 'archived' && !customer.is_archived) return false;
    
    // Service type filter
    if (filterServiceType !== 'all') {
      const serviceTypes = getCustomerServiceTypes(customer);
      if (!serviceTypes.includes(filterServiceType)) return false;
    }
    
    // Date range filter
    if (filterDateRange !== 'all' && customer.last_booking_date) {
      const lastBooking = new Date(customer.last_booking_date);
      const now = new Date();
      const daysDiff = Math.floor((now - lastBooking) / (1000 * 60 * 60 * 24));
      
      if (filterDateRange === 'week' && daysDiff > 7) return false;
      if (filterDateRange === 'month' && daysDiff > 30) return false;
      if (filterDateRange === '3months' && daysDiff > 90) return false;
      if (filterDateRange === 'older' && daysDiff <= 90) return false;
    }
    
    return true;
  });

  const openCustomerFile = async (customer) => {
    setSelectedCustomer(customer);
    setFileLoading(true);
    setActiveSection('overview');
    try {
      const response = await api.get(`/customer/files/${customer.id}`);
      setCustomerFile(response.data);
    } catch (error) {
      toast.error('Failed to load customer file');
    } finally {
      setFileLoading(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await api.post(`/customer/files/${selectedCustomer.id}/notes`, {
        content: newNote,
        note_type: 'general',
        is_important: false
      });
      toast.success('Note added');
      setNewNote('');
      // Refresh the file
      const response = await api.get(`/customer/files/${selectedCustomer.id}`);
      setCustomerFile(response.data);
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const sections = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'forms', label: 'Forms & Waivers', icon: FileText },
    { id: 'addresses', label: 'Addresses', icon: MapPin },
    { id: 'family', label: 'Family', icon: Users },
    { id: 'notes', label: 'Notes', icon: StickyNote }
  ];

  // Customer List View
  if (!selectedCustomer) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Customer Files</h1>
            <p className="text-[var(--text-muted)] mt-1">Complete records for all customers ({filteredCustomers.length})</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowFilters(!showFilters)} variant="outline" size="sm">
              <Filter className="w-4 h-4 mr-2" />Filters
            </Button>
            <Button onClick={fetchCustomers} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
          </div>
        </div>

        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="card p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Status Filter */}
                  <div>
                    <label className="text-sm font-medium text-[var(--text-muted)] mb-1 block">Status</label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="input-styled w-full"
                      data-testid="filter-status"
                    >
                      <option value="all">All Customers</option>
                      <option value="active">Active</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  
                  {/* Service Type Filter */}
                  <div>
                    <label className="text-sm font-medium text-[var(--text-muted)] mb-1 block">Service Type</label>
                    <select
                      value={filterServiceType}
                      onChange={(e) => setFilterServiceType(e.target.value)}
                      className="input-styled w-full"
                      data-testid="filter-service"
                    >
                      <option value="all">All Services</option>
                      <option value="rides">Rides</option>
                      <option value="errands">Errands</option>
                      <option value="courier">Courier</option>
                      <option value="laundry">Laundry</option>
                    </select>
                  </div>
                  
                  {/* Date Range Filter */}
                  <div>
                    <label className="text-sm font-medium text-[var(--text-muted)] mb-1 block">Last Booking</label>
                    <select
                      value={filterDateRange}
                      onChange={(e) => setFilterDateRange(e.target.value)}
                      className="input-styled w-full"
                      data-testid="filter-date"
                    >
                      <option value="all">Any Time</option>
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                      <option value="3months">Last 3 Months</option>
                      <option value="older">Older than 3 Months</option>
                    </select>
                  </div>
                  
                  {/* Clear Filters */}
                  <div className="flex items-end">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setFilterStatus('active');
                        setFilterServiceType('all');
                        setFilterDateRange('all');
                      }}
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bulk Actions Bar */}
        {selectedCustomers.length > 0 && (
          <div className="card p-3 mb-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <div className="flex items-center justify-between">
              <span className="font-medium">{selectedCustomers.length} selected</span>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleBulkArchive}
                  data-testid="bulk-archive-btn"
                >
                  <Archive className="w-4 h-4 mr-1" />Archive Selected
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleBulkDelete}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />Delete Selected
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedCustomers([])}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, or phone..."
            className="pl-10"
          />
        </div>

        {/* Select All Checkbox */}
        {filteredCustomers.length > 0 && (
          <div className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              checked={selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0}
              onChange={selectAllCustomers}
              className="w-4 h-4 rounded"
              data-testid="select-all-checkbox"
            />
            <span className="text-sm text-[var(--text-muted)]">Select all</span>
          </div>
        )}

        {/* Customer List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-12 bg-[var(--background-subtle)] rounded-xl">
            <User className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-[var(--text-muted)]">No customers found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredCustomers.map(customer => {
              const serviceTypes = getCustomerServiceTypes(customer);
              const isSelected = selectedCustomers.includes(customer.id);
              return (
                <div
                  key={customer.id}
                  className={`card p-4 hover:shadow-md transition-shadow cursor-pointer border-l-4 ${
                    customer.is_archived ? 'border-gray-400 opacity-75' : 'border-[var(--primary)]'
                  } ${isSelected ? 'ring-2 ring-[#D4AF37]' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Selection Checkbox */}
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleCustomerSelection(customer.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 rounded flex-shrink-0"
                      data-testid={`select-customer-${customer.id}`}
                    />
                    
                    <div 
                      className="flex-1 flex items-center justify-between"
                      onClick={() => openCustomerFile(customer)}
                    >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[var(--primary)]/10 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-[var(--primary)]" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{customer.full_name}</h3>
                          {/* Role Badge */}
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30">
                            Customer
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-[var(--text-muted)]">
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{customer.email}</span>
                          {customer.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{customer.phone}</span>}
                        </div>
                        {/* Service Type Badges */}
                        {serviceTypes.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-2">
                            {serviceTypes.map(serviceId => {
                              const badge = customerServiceBadges[serviceId];
                              if (!badge) return null;
                              return (
                                <span 
                                  key={serviceId}
                                  className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${badge.color}`}
                                >
                                  <badge.icon className="w-3 h-3" />
                                  {badge.label}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-[var(--primary)]">{customer.booking_count || 0}</p>
                        <p className="text-xs text-[var(--text-muted)]">Bookings</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-[var(--success)]">{formatCurrency(customer.total_spent)}</p>
                        <p className="text-xs text-[var(--text-muted)]">Total Spent</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-[var(--text-muted)]">Last Booking</p>
                        <p className="text-sm font-medium">{formatDate(customer.last_booking)}</p>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {customer.is_archived ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRestoreCustomer(customer.id)}
                            className="text-[var(--success)]"
                            title="Restore"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleArchiveCustomer(customer.id)}
                            className="text-[var(--text-muted)]"
                            title="Archive"
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCustomer(customer.id)}
                          className="text-[var(--error)]"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    );
  }

  // Customer File Detail View
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => { setSelectedCustomer(null); setCustomerFile(null); setEditMode(false); }}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold font-['Cormorant_Garamond']">{selectedCustomer.full_name}</h1>
          <p className="text-[var(--text-muted)]">Customer File</p>
        </div>
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
              <Button onClick={handleSaveCustomer} className="btn-primary">
                <Save className="w-4 h-4 mr-1" /> Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={handleEditCustomer}>
                <Edit className="w-4 h-4 mr-1" /> Edit
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-[var(--error)] hover:text-red-700"
                onClick={() => handleArchiveCustomer(selectedCustomer.id)}
              >
                <Archive className="w-4 h-4 mr-1" /> Archive
              </Button>
            </>
          )}
        </div>
      </div>

      {fileLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
        </div>
      ) : customerFile && (
        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6">
          {/* Mobile Section Tabs */}
          <div className="lg:hidden overflow-x-auto pb-2 -mx-2 px-2">
            <div className="flex gap-2 min-w-max">
              {sections.map(section => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeSection === section.id 
                      ? 'bg-[var(--primary)] text-white' 
                      : 'bg-[var(--background-subtle)] text-[var(--text-secondary)]'
                  }`}
                >
                  <section.icon className="w-4 h-4" />
                  {section.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sidebar Navigation - Desktop Only */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="bg-[var(--background-paper)] rounded-xl shadow-sm border p-4 sticky top-4">
              {/* Customer Quick Info */}
              <div className="text-center pb-4 border-b mb-4">
                <div className="w-20 h-20 bg-[var(--primary)]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <User className="w-10 h-10 text-[var(--primary)]" />
                </div>
                <h3 className="font-bold">{customerFile.customer.full_name}</h3>
                <p className="text-sm text-[var(--text-muted)]">{customerFile.customer.email}</p>
                {customerFile.customer.phone && (
                  <p className="text-sm text-[var(--text-muted)]">{customerFile.customer.phone}</p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  Customer since {formatDate(customerFile.customer.created_at)}
                </p>
              </div>

              {/* Navigation */}
              <nav className="space-y-1">
                {sections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeSection === section.id 
                        ? 'bg-[var(--primary)] text-white' 
                        : 'hover:bg-[var(--background-subtle)] text-[var(--text-primary)]'
                    }`}
                  >
                    <section.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{section.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9">
            {/* Edit Mode Form */}
            {editMode && (
              <div className="bg-[var(--background-paper)] rounded-xl p-6 shadow-sm border mb-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Edit className="w-5 h-5 text-[var(--primary)]" />
                  Edit Customer Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Full Name</label>
                    <Input
                      value={editData.full_name || ''}
                      onChange={(e) => setEditData({...editData, full_name: e.target.value})}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <Input
                      type="email"
                      value={editData.email || ''}
                      onChange={(e) => setEditData({...editData, email: e.target.value})}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <Input
                      type="tel"
                      value={editData.phone || ''}
                      onChange={(e) => setEditData({...editData, phone: e.target.value})}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Address</label>
                    <Input
                      value={editData.address || ''}
                      onChange={(e) => setEditData({...editData, address: e.target.value})}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">City</label>
                    <Input
                      value={editData.city || ''}
                      onChange={(e) => setEditData({...editData, city: e.target.value})}
                      className="w-full"
                    />
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">State</label>
                      <Input
                        value={editData.state || ''}
                        onChange={(e) => setEditData({...editData, state: e.target.value})}
                        className="w-full"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">Zip Code</label>
                      <Input
                        value={editData.zip_code || ''}
                        onChange={(e) => setEditData({...editData, zip_code: e.target.value})}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Overview Section */}
            {activeSection === 'overview' && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                  <div className="bg-[var(--background-paper)] rounded-xl p-3 lg:p-4 shadow-sm border">
                    <div className="flex items-center gap-2 lg:gap-3">
                      <div className="p-1.5 lg:p-2 bg-[#D4AF37]/10 rounded-lg">
                        <Calendar className="w-4 h-4 lg:w-5 lg:h-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="text-xl lg:text-2xl font-bold">{customerFile.summary.total_bookings}</p>
                        <p className="text-[10px] lg:text-xs text-[var(--text-muted)]">Total Bookings</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-[var(--background-paper)] rounded-xl p-3 lg:p-4 shadow-sm border">
                    <div className="flex items-center gap-2 lg:gap-3">
                      <div className="p-1.5 lg:p-2 bg-[var(--success)]/15 rounded-lg">
                        <DollarSign className="w-4 h-4 lg:w-5 lg:h-5 text-[var(--success)]" />
                      </div>
                      <div>
                        <p className="text-xl lg:text-2xl font-bold">{formatCurrency(customerFile.summary.total_spent)}</p>
                        <p className="text-[10px] lg:text-xs text-[var(--text-muted)]">Total Spent</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-[var(--background-paper)] rounded-xl p-3 lg:p-4 shadow-sm border">
                    <div className="flex items-center gap-2 lg:gap-3">
                      <div className="p-1.5 lg:p-2 bg-[#F4C2C2]/15 rounded-lg">
                        <FileText className="w-4 h-4 lg:w-5 lg:h-5 text-[#F4C2C2]" />
                      </div>
                      <div>
                        <p className="text-xl lg:text-2xl font-bold">{customerFile.summary.forms_signed}</p>
                        <p className="text-[10px] lg:text-xs text-[var(--text-muted)]">Forms Signed</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-[var(--background-paper)] rounded-xl p-3 lg:p-4 shadow-sm border">
                    <div className="flex items-center gap-2 lg:gap-3">
                      <div className="p-1.5 lg:p-2 bg-amber-100 rounded-lg">
                        <Star className="w-4 h-4 lg:w-5 lg:h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xl lg:text-2xl font-bold">{customerFile.summary.reviews_given}</p>
                        <p className="text-[10px] lg:text-xs text-[var(--text-muted)]">Reviews Given</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-[var(--background-paper)] rounded-xl p-6 shadow-sm border">
                  <h3 className="font-semibold mb-4">Customer Timeline</h3>
                  <div className="flex items-center justify-between text-sm">
                    <div className="text-center">
                      <p className="text-[var(--text-muted)]">First Booking</p>
                      <p className="font-medium">{formatDate(customerFile.summary.first_booking_date)}</p>
                    </div>
                    <div className="flex-1 h-1 bg-[var(--background-subtle)] mx-4 relative">
                      <div className="absolute left-0 w-3 h-3 bg-green-500 rounded-full -top-1"></div>
                      <div className="absolute right-0 w-3 h-3 bg-[#D4AF37]/100 rounded-full -top-1"></div>
                    </div>
                    <div className="text-center">
                      <p className="text-[var(--text-muted)]">Last Booking</p>
                      <p className="font-medium">{formatDate(customerFile.summary.last_booking_date)}</p>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-[var(--background-paper)] rounded-xl p-6 shadow-sm border">
                  <h3 className="font-semibold mb-4">Recent Bookings</h3>
                  {customerFile.bookings.slice(0, 5).map((booking, idx) => (
                    <div key={booking.id || idx} className="flex items-center justify-between py-3 border-b last:border-0">
                      <div>
                        <p className="font-medium">{booking.service_name || 'Service'}</p>
                        <p className="text-sm text-[var(--text-muted)]">{formatDate(booking.scheduled_date || booking.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          booking.status === 'completed' ? 'bg-[var(--success)]/15 text-green-700' :
                          booking.status === 'cancelled' ? 'bg-[var(--error)]/15 text-red-700' :
                          'bg-[var(--warning)]/15 text-yellow-700'
                        }`}>{booking.status}</span>
                        <span className="font-medium">{formatCurrency(booking.total_amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bookings Section */}
            {activeSection === 'bookings' && (
              <div className="bg-[var(--background-paper)] rounded-xl shadow-sm border">
                <div className="p-4 border-b">
                  <h3 className="font-semibold">All Bookings ({customerFile.bookings.length})</h3>
                </div>
                <div className="divide-y">
                  {customerFile.bookings.map((booking, idx) => (
                    <div key={booking.id || idx} className="p-4 hover:bg-[var(--background-subtle)]">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{booking.service_name || 'Service'}</p>
                          <p className="text-sm text-[var(--text-muted)]">
                            {formatDate(booking.scheduled_date || booking.created_at)} at {booking.scheduled_time || 'TBD'}
                          </p>
                          {booking.worker_name && (
                            <p className="text-sm text-[var(--text-muted)]">Worker: {booking.worker_name}</p>
                          )}
                          {booking.address && (
                            <p className="text-sm text-gray-400 flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />{booking.address}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-sm ${
                            booking.status === 'completed' ? 'bg-[var(--success)]/15 text-green-700' :
                            booking.status === 'cancelled' ? 'bg-[var(--error)]/15 text-red-700' :
                            booking.status === 'confirmed' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                            'bg-[var(--warning)]/15 text-yellow-700'
                          }`}>{booking.status}</span>
                          <p className="font-bold mt-2">{formatCurrency(booking.total_amount)}</p>
                          <p className="text-xs text-gray-400">{booking.payment_status || 'pending'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {customerFile.bookings.length === 0 && (
                    <div className="p-8 text-center text-[var(--text-muted)]">No bookings yet</div>
                  )}
                </div>
              </div>
            )}

            {/* Payments Section */}
            {activeSection === 'payments' && (
              <div className="bg-[var(--background-paper)] rounded-xl shadow-sm border">
                <div className="p-4 border-b">
                  <h3 className="font-semibold">Payment History ({customerFile.payments.length})</h3>
                </div>
                <div className="divide-y">
                  {customerFile.payments.map((payment, idx) => (
                    <div key={payment.id || idx} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{payment.description || 'Payment'}</p>
                        <p className="text-sm text-[var(--text-muted)]">{formatDate(payment.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[var(--success)]">{formatCurrency(payment.amount)}</p>
                        <p className="text-xs text-gray-400">{payment.method || 'Card'}</p>
                      </div>
                    </div>
                  ))}
                  {customerFile.payments.length === 0 && (
                    <div className="p-8 text-center text-[var(--text-muted)]">No payments recorded</div>
                  )}
                </div>
              </div>
            )}

            {/* Forms Section */}
            {activeSection === 'forms' && (
              <div className="bg-[var(--background-paper)] rounded-xl shadow-sm border">
                <div className="p-4 border-b">
                  <h3 className="font-semibold">Signed Forms & Waivers ({customerFile.signed_forms.length})</h3>
                </div>
                <div className="divide-y">
                  {customerFile.signed_forms.map((form, idx) => (
                    <div key={form.id || idx} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#F4C2C2]/15 rounded-lg">
                          <FileText className="w-5 h-5 text-[#F4C2C2]" />
                        </div>
                        <div>
                          <p className="font-medium">{form.template_name}</p>
                          <p className="text-sm text-[var(--text-muted)]">Signed {formatDate(form.created_at)}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        form.status === 'approved' ? 'bg-[var(--success)]/15 text-green-700' : 'bg-[var(--warning)]/15 text-yellow-700'
                      }`}>{form.status}</span>
                    </div>
                  ))}
                  {customerFile.signed_forms.length === 0 && (
                    <div className="p-8 text-center text-[var(--text-muted)]">No forms signed yet</div>
                  )}
                </div>
              </div>
            )}

            {/* Addresses Section */}
            {activeSection === 'addresses' && (
              <div className="bg-[var(--background-paper)] rounded-xl shadow-sm border">
                <div className="p-4 border-b">
                  <h3 className="font-semibold">Saved Addresses ({customerFile.addresses.length})</h3>
                </div>
                <div className="divide-y">
                  {customerFile.addresses.map((address, idx) => (
                    <div key={address.id || idx} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-[#D4AF37]/10 rounded-lg">
                          <Home className="w-5 h-5 text-[#D4AF37]" />
                        </div>
                        <div>
                          <p className="font-medium">{address.label}</p>
                          <p className="text-sm text-[var(--text-secondary)]">{address.address}</p>
                          <p className="text-sm text-[var(--text-muted)]">{address.city}, {address.state} {address.zip_code}</p>
                          {address.special_instructions && (
                            <p className="text-sm text-gray-400 mt-1 italic">{address.special_instructions}</p>
                          )}
                        </div>
                        {address.is_default && (
                          <span className="ml-auto px-2 py-1 bg-[var(--success)]/15 text-green-700 text-xs rounded-full">Default</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {customerFile.addresses.length === 0 && (
                    <div className="p-8 text-center text-[var(--text-muted)]">No saved addresses</div>
                  )}
                </div>
              </div>
            )}

            {/* Family Section */}
            {activeSection === 'family' && (
              <div className="bg-[var(--background-paper)] rounded-xl shadow-sm border">
                <div className="p-4 border-b">
                  <h3 className="font-semibold">Family Members ({customerFile.family_members.length})</h3>
                </div>
                <div className="divide-y">
                  {customerFile.family_members.map((member, idx) => (
                    <div key={member.id || idx} className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-sm text-[var(--text-muted)]">{member.relationship}</p>
                        {member.phone && <p className="text-sm text-gray-400">{member.phone}</p>}
                      </div>
                    </div>
                  ))}
                  {customerFile.family_members.length === 0 && (
                    <div className="p-8 text-center text-[var(--text-muted)]">No family members added</div>
                  )}
                </div>
              </div>
            )}

            {/* Notes Section */}
            {activeSection === 'notes' && (
              <div className="space-y-4">
                {/* Add Note */}
                <div className="bg-[var(--background-paper)] rounded-xl p-4 shadow-sm border">
                  <h3 className="font-semibold mb-3">Add Note</h3>
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note about this customer..."
                    rows={3}
                    className="mb-3"
                  />
                  <Button onClick={addNote} disabled={addingNote || !newNote.trim()}>
                    <Plus className="w-4 h-4 mr-2" />{addingNote ? 'Adding...' : 'Add Note'}
                  </Button>
                </div>

                {/* Notes List */}
                <div className="bg-[var(--background-paper)] rounded-xl shadow-sm border">
                  <div className="p-4 border-b">
                    <h3 className="font-semibold">Notes ({customerFile.notes.length})</h3>
                  </div>
                  <div className="divide-y">
                    {customerFile.notes.map((note, idx) => (
                      <div key={note.id || idx} className="p-4">
                        <div className="flex items-start justify-between">
                          <p className="text-[var(--text-primary)]">{note.content}</p>
                          {note.is_important && (
                            <span className="px-2 py-1 bg-[var(--error)]/15 text-red-700 text-xs rounded">Important</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          By {note.created_by_name} • {formatDate(note.created_at)}
                        </p>
                      </div>
                    ))}
                    {customerFile.notes.length === 0 && (
                      <div className="p-8 text-center text-[var(--text-muted)]">No notes yet</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default CustomerFilesTab;
